import { useState, type FormEvent, type ReactNode } from 'react';
import { V2Alert, V2Button, V2FormField, V2Modal, V2Switch } from '@sakhtar/design-system/react';
import { apiPut } from '../../../lib/apiClient.js';
import {
  OCCASION_LABELS,
  ORDER_EVENT_LABELS,
  compatibleTemplates,
  faNum,
  hourLabel,
  previewBody,
} from './smsModel.ts';
import { smsErrorText } from './smsApi.js';
import type {
  SmsRoutine,
  SmsRoutineSettings,
  SmsRoutineSpec,
  SmsTemplate,
  SmsVariable,
} from './smsTypes.ts';

const BEFORE_OPTIONS = [0, 1, 2, 3, 7, 14] as const;
const AFTER_OPTIONS = [1, 2, 3, 7, 14, 30] as const;
const FROM_HOURS = [6, 7, 8, 9, 10, 11, 12] as const;
const TO_HOURS = [17, 18, 19, 20, 21, 22, 23] as const;

function toggle<T>(list: readonly T[], value: T, max = 3): readonly T[] {
  if (list.includes(value)) return list.filter((v) => v !== value);
  return list.length >= max ? list : [...list, value];
}

function Chips<T extends string | number>(props: {
  readonly label: string;
  readonly options: readonly T[];
  readonly selected: readonly T[];
  readonly render: (v: T) => string;
  readonly onToggle: (v: T) => void;
  readonly hint?: string;
}) {
  return (
    <fieldset className="sms-chips">
      <legend>{props.label}</legend>
      <div>
        {props.options.map((o) => (
          <button
            key={String(o)}
            type="button"
            className="sms-chip"
            aria-pressed={props.selected.includes(o)}
            onClick={() => props.onToggle(o)}
          >
            {props.render(o)}
          </button>
        ))}
      </div>
      {props.hint ? <small>{props.hint}</small> : null}
    </fieldset>
  );
}

export interface SmsRoutineEditorProps {
  readonly base: string;
  readonly spec: SmsRoutineSpec;
  readonly routine: SmsRoutine;
  readonly templates: readonly SmsTemplate[];
  readonly variables: readonly SmsVariable[];
  readonly onClose: () => void;
  readonly onSaved: () => void;
  /** Open the template editor to create one for this routine. */
  readonly onCreateTemplate: () => void;
}

export function SmsRoutineEditor(props: SmsRoutineEditorProps) {
  const { spec } = props;
  const [templateId, setTemplateId] = useState<string>(props.routine.templateId ?? '');
  const [enabled, setEnabled] = useState(props.routine.enabled);
  const [settings, setSettings] = useState<SmsRoutineSettings>(props.routine.settings);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options = compatibleTemplates(props.templates, spec);
  const selected = props.templates.find((t) => t.id === templateId) ?? null;

  const patch = (change: Record<string, unknown>) =>
    setSettings((s) => ({ ...s, ...change }) as SmsRoutineSettings);

  let timing: ReactNode = null;
  if (settings.kind === 'invoice_due') {
    const s = settings;
    timing = (
      <>
        <Chips
          label="یادآوری پیش از سررسید"
          options={BEFORE_OPTIONS}
          selected={s.beforeDueDays}
          render={(d) => (d === 0 ? 'روز سررسید' : `${faNum(d)} روز قبل`)}
          onToggle={(d) => patch({ beforeDueDays: toggle(s.beforeDueDays, d) })}
          hint="حداکثر ۳ زمان"
        />
        <Chips
          label="یادآوری پس از سررسید"
          options={AFTER_OPTIONS}
          selected={s.afterDueDays}
          render={(d) => `${faNum(d)} روز بعد`}
          onToggle={(d) => patch({ afterDueDays: toggle(s.afterDueDays, d) })}
          hint="برای فاکتورهای هنوز تسویه‌نشده؛ حداکثر ۳ زمان"
        />
      </>
    );
  } else if (settings.kind === 'order_payment') {
    const s = settings;
    timing = (
      <Chips
        label="چه زمانی پیامک برود؟"
        options={['order_confirmed', 'payment_received'] as const}
        selected={s.events}
        render={(e) => ORDER_EVENT_LABELS[e]}
        onToggle={(e) => patch({ events: toggle(s.events, e, 2) })}
      />
    );
  } else if (settings.kind === 'followup') {
    const s = settings;
    timing = (
      <div className="sms-followup">
        <V2Switch
          label="یادآوری فعالیت‌های موعددار"
          checked={s.activityRemindHours !== null}
          onCheckedChange={(on) => patch({ activityRemindHours: on ? 24 : null })}
        />
        {s.activityRemindHours !== null ? (
          <label className="sms-inline">
            <input
              className="sk-input ltr sk-num"
              type="number"
              min={1}
              max={72}
              value={s.activityRemindHours}
              onChange={(e) => patch({ activityRemindHours: Number(e.target.value) || 1 })}
            />
            ساعت پیش از موعد
          </label>
        ) : null}
        <V2Switch
          label="لیدهایی که مدتی تماس نگرفته‌اند"
          checked={s.leadNoContactDays !== null}
          onCheckedChange={(on) => patch({ leadNoContactDays: on ? 7 : null })}
        />
        {s.leadNoContactDays !== null ? (
          <label className="sms-inline">
            <input
              className="sk-input ltr sk-num"
              type="number"
              min={1}
              max={60}
              value={s.leadNoContactDays}
              onChange={(e) => patch({ leadNoContactDays: Number(e.target.value) || 1 })}
            />
            روز بدون تماس
          </label>
        ) : null}
      </div>
    );
  } else {
    const s = settings;
    timing = (
      <Chips
        label="مناسبت‌ها"
        options={['birthday', 'nowruz', 'yalda'] as const}
        selected={s.occasions}
        render={(o) => OCCASION_LABELS[o]}
        onToggle={(o) => patch({ occasions: toggle(s.occasions, o, 3) })}
      />
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await apiPut(`${props.base}/api/v1/crm/sms/routines/${spec.kind}`, {
        templateId: templateId || null,
        enabled,
        settings,
      });
      props.onSaved();
    } catch (err) {
      setError(smsErrorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <V2Modal
      open
      title={spec.title}
      size="lg"
      onClose={() => (busy ? undefined : props.onClose())}
      footer={
        <>
          <V2Button variant="secondary" onClick={props.onClose} disabled={busy}>
            انصراف
          </V2Button>
          <V2Button variant="primary" type="submit" form="sms-routine-form" disabled={busy}>
            {busy ? 'در حال ذخیره…' : 'ذخیره روال'}
          </V2Button>
        </>
      }
    >
      <form
        id="sms-routine-form"
        className="sms-editor"
        onSubmit={(e) => void submit(e)}
        noValidate
      >
        <p className="sms-editor__lead">{spec.description}</p>
        {error ? <V2Alert tone="danger" title={error} /> : null}
        {spec.requiresMarketingConsent ? (
          <V2Alert tone="warn" title="فقط برای مشتریانی که رضایت بازاریابی داده‌اند">
            پیام تبلیغاتی بدون ثبت رضایت مشتری ارسال نمی‌شود. رضایت هر مشتری در پروندهٔ او مدیریت
            می‌شود.
          </V2Alert>
        ) : null}

        <V2FormField label="قالب پیامک" required>
          <select
            className="sk-select"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
          >
            <option value="">انتخاب قالب</option>
            {options.map((t) => (
              <option key={t.id} value={t.id}>
                {t.title}
              </option>
            ))}
          </select>
        </V2FormField>
        {options.length === 0 ? (
          <p className="sms-editor__empty">
            قالب سازگاری وجود ندارد.{' '}
            <button type="button" className="sms-link" onClick={props.onCreateTemplate}>
              ساخت قالب برای این روال
            </button>
          </p>
        ) : null}
        {selected ? (
          <div className="sms-editor__preview">
            <span className="sms-editor__preview-label">پیش‌نمایش با داده‌ٔ نمونه</span>
            <div className="sms-bubble">{previewBody(selected.body, props.variables)}</div>
          </div>
        ) : null}

        <div className="sms-editor__section">
          <h3>زمان‌بندی</h3>
          {timing}
          <div className="sms-window">
            <span>ارسال فقط بین ساعت</span>
            <select
              className="sk-select"
              aria-label="از ساعت"
              value={settings.sendWindow.fromHour}
              onChange={(e) =>
                patch({ sendWindow: { ...settings.sendWindow, fromHour: Number(e.target.value) } })
              }
            >
              {FROM_HOURS.map((h) => (
                <option key={h} value={h}>
                  {hourLabel(h)}
                </option>
              ))}
            </select>
            <span>تا</span>
            <select
              className="sk-select"
              aria-label="تا ساعت"
              value={settings.sendWindow.toHour}
              onChange={(e) =>
                patch({ sendWindow: { ...settings.sendWindow, toHour: Number(e.target.value) } })
              }
            >
              {TO_HOURS.map((h) => (
                <option key={h} value={h}>
                  {hourLabel(h)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <V2Switch
          label="فعال‌سازی روال"
          description="ارسال خودکار هنوز متصل نیست؛ فعال‌سازی فقط تنظیم را آماده نگه می‌دارد و پیامکی ارسال نمی‌شود."
          checked={enabled}
          disabled={!templateId}
          onCheckedChange={setEnabled}
        />
      </form>
    </V2Modal>
  );
}
